import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'
import { useBooleanFlagValue, useFlag } from '@openfeature/react-sdk'
import { light } from '@fortawesome/fontawesome-svg-core/import.macro'

import { useAnalyticsTrack } from '@/features/amplitude'
import { FEATURE_FLAG_KEYS } from '@/flags'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useEndDateForFinancialInsights } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'

import { InsightOverviewCards } from '@/features/financial-insights/practice-insights/insight-overview-card'
import { InsightsTrendGraph } from '@/features/financial-insights/practice-insights/InsightsTrendGraph'
import { IncomeGoalCardV2 } from '@/features/user/dashboard/insights-cards/IncomeGoalCardV2'
import { TaxDeductionTipCard } from '@/features/user/dashboard/insights-cards/TaxDeductionTipCard'
import { TypographyStyles } from '@/styles'
import { Button, Card, Icon, Text } from '@/ui'
import PracticeOverviewAlert from './PracticeOverviewAlerts/PracticeOverviewAlert'

// TODO: Migrate to V2 skipcq: JS-0099
import { fetchInsightsBarchartData } from '@/componentsV1/Finances/shared/financeActions'
import {
  getInsightsDateRange,
  INSIGHTS_TYPE,
  processDataForChart,
} from '@/componentsV1/Finances/shared/utils'

export const PracticeOverviewCard = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()
  const currentDate = useMemo(() => DateTime.now(), [])
  const endDate = useEndDateForFinancialInsights(currentDate)
  const dateRange = useMemo(() => getInsightsDateRange(endDate), [endDate])

  const showLever1EarlyAccessToCentralizedWeeklyDashboardMvp =
    useBooleanFlagValue(
      FEATURE_FLAG_KEYS.enableLever1EarlyAccessToCentralizedWeeklyDashboardMvp,
      false
    )

  const [trendData, setTrendData] = useState<
    { [key: string]: string | number | undefined }[]
  >([])

  const { value: enableTaxDeductionsDashboard } = useFlag(
    FEATURE_FLAG_KEYS.enableTaxDeductionsDashboard,
    false
  )

  useEffect(() => {
    const fetchTrendData = async () => {
      const [startDate, endDate] = dateRange

      const fetchedData = await fetchInsightsBarchartData(
        INSIGHTS_TYPE.PROFIT,
        startDate,
        endDate
      )(dispatch)

      if (fetchedData) {
        const chartData = processDataForChart(
          fetchedData,
          dateRange,
          INSIGHTS_TYPE.PROFIT
        )
        setTrendData(chartData)
      }
    }
    fetchTrendData()
  }, [dispatch, dateRange])

  const dateRangeString = useMemo(() => {
    const [startDate, endDate] = dateRange

    const start = DateTime.fromFormat(
      startDate,
      DATE_FORMATS_LUXON.TIMESTAMP
    ).toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)

    const end = DateTime.fromFormat(
      endDate,
      DATE_FORMATS_LUXON.TIMESTAMP
    ).toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)
    return `Last 12 months (${start} - ${end})`
  }, [dateRange])

  return (
    <Card>
      <div className="flex flex-col gap-6">
        <div className="flex flow-row items-center justify-between">
          <Text variant={TypographyStyles.H2} className="w-5/8">
            {endDate.toFormat(DATE_FORMATS_LUXON.MONTH_YEAR_LONG)} Practice
            Overview
          </Text>

          <div className="w-3/8 flex justify-end items-end">
            <Button
              variant="link"
              onClick={() => {
                track('Clicked Practice Insights View Reports Button')
                navigate('/insights/overview')
              }}
            >
              View Reports
              <Icon icon={light('arrow-right')} style={{ marginLeft: 8 }} />
            </Button>
          </div>
        </div>
        {showLever1EarlyAccessToCentralizedWeeklyDashboardMvp && (
          <PracticeOverviewAlert />
        )}
        <InsightOverviewCards year={endDate.year} month={endDate.month} />

        <div className="flex items-center justify-between mt-6">
          <Text variant={TypographyStyles.H2} className="w-5/8">
            Practice Finances Over Time
          </Text>

          <Text variant={TypographyStyles.BODY_SM} className="w-3/8 text-right">
            {dateRangeString}
          </Text>
        </div>

        <InsightsTrendGraph data={trendData} />

        {enableTaxDeductionsDashboard ? (
          <IncomeGoalCardV2 />
        ) : (
          <div className="flex gap-4">
            <div className="w-3/8">
              <IncomeGoalCardV2 />
            </div>

            <div className="w-5/8">
              <TaxDeductionTipCard />
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}
